
import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  Bell, 
  TrendingUp, 
  AlertTriangle, 
  Brain, 
  Eye, 
  EyeOff,
  X,
  Clock,
  CheckCircle
} from 'lucide-react';
import { useProactiveInsights } from '@/hooks/useProactiveInsights';
import { formatDistanceToNow } from 'date-fns';

export const ProactiveInsights = () => {
  const { 
    insights, 
    unreadCount, 
    markAsRead, 
    dismissInsight 
  } = useProactiveInsights();
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);

  const filteredInsights = showOnlyUnread 
    ? insights.filter(insight => !insight.isRead)
    : insights;

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'alert':
        return <AlertTriangle className="h-5 w-5" />;
      case 'trend':
        return <TrendingUp className="h-5 w-5" />;
      case 'anomaly':
        return <Bell className="h-5 w-5" />;
      case 'recommendation':
        return <Brain className="h-5 w-5" />;
      default:
        return <Bell className="h-5 w-5" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleInsightClick = (insight: any) => {
    if (!insight.isRead) {
      markAsRead(insight.id);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bell className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-xl font-semibold">Proactive Insights</h2>
            <p className="text-sm text-muted-foreground">
              AI-generated insights and alerts
            </p>
          </div>
          {unreadCount > 0 && (
            <Badge variant="destructive" className="ml-2">
              {unreadCount}
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant={showOnlyUnread ? "default" : "outline"}
            size="sm"
            onClick={() => setShowOnlyUnread(!showOnlyUnread)}
            className="flex items-center gap-2"
          >
            {showOnlyUnread ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            {showOnlyUnread ? 'Show All' : 'Unread Only'}
          </Button>
        </div>
      </div>

      <ScrollArea className="h-96">
        <div className="space-y-4">
          {filteredInsights.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Insights Yet</h3>
              <p className="text-muted-foreground">
                {showOnlyUnread 
                  ? "All insights have been read" 
                  : "Start uploading data to receive AI-powered insights and alerts"
                }
              </p>
            </div>
          ) : (
            filteredInsights.map((insight) => (
              <Card 
                key={insight.id} 
                className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                  !insight.isRead ? 'ring-2 ring-primary/20 bg-primary/5' : ''
                }`}
                onClick={() => handleInsightClick(insight)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-full ${
                      insight.severity === 'critical' ? 'bg-red-100 text-red-600' :
                      insight.severity === 'high' ? 'bg-orange-100 text-orange-600' :
                      insight.severity === 'medium' ? 'bg-yellow-100 text-yellow-600' :
                      'bg-blue-100 text-blue-600'
                    }`}>
                      {getInsightIcon(insight.insightType)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className={`font-medium ${!insight.isRead ? 'font-semibold' : ''}`}>
                          {insight.title}
                        </h3>
                        <Badge 
                          variant="outline" 
                          className={getSeverityColor(insight.severity)}
                        >
                          {insight.severity}
                        </Badge>
                        {!insight.isRead && (
                          <div className="w-2 h-2 bg-primary rounded-full" />
                        )}
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-3">
                        {insight.description}
                      </p>
                      
                      {insight.actionItems.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-gray-700">Suggested Actions:</p>
                          <ul className="text-xs text-muted-foreground space-y-1">
                            {insight.actionItems.slice(0, 3).map((item, index) => (
                              <li key={index} className="flex items-start gap-1">
                                <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                {item}
                              </li>
                            ))}
                            {insight.actionItems.length > 3 && (
                              <li className="text-xs text-primary">
                                +{insight.actionItems.length - 3} more actions
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(insight.createdAt), { addSuffix: true })}
                        <span className="capitalize">• {insight.insightType}</span>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissInsight(insight.id);
                    }}
                    className="ml-2 opacity-60 hover:opacity-100"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>
      </ScrollArea>
    </Card>
  );
};
